<template>
  <div class="main-wrapper">
    <layout-header />

    <div class="content container">
      <div class="px-3">
        <div class="row">
          <div class="col-lg-3 d-lg-block d-none theiaStickySidebar mb-3">
            <div class="stickyside">
              <div class="rounded-xl d-none d-lg-block shadow-sm p-2"><vendor-sidebar /></div>
            </div>
          </div>

          <div class="col-lg-9 col-md-12 mb-5">
            <div class="d-flex flex-wrap justify-content-between align-items-center border-bottom mb-3 pb-2">
              <div>
                <h2 class="mb-1">Gestion des utilisateurs</h2>
              </div>
              <div class="d-flex align-items-center">
                <button class="btn btn-outline-secondary mr-2" :disabled="loading" @click="fetchUsers">
                  <span class="spinner-border spinner-border-sm mr-1" role="status" aria-hidden="true" v-if="loading"></span>
                  Recharger
                </button>
              </div>
            </div>

            <div class="row mb-3">
              <div class="col-md-6 mb-2">
                <label class="mb-1 text-muted text-sm">Rechercher (nom, email, téléphone)</label>
                <input
                  type="text"
                  class="form-control"
                  v-model="searchTerm"
                  placeholder="Tapez pour filtrer..."
                />
              </div>
              <div class="col-md-3 mb-2">
                <label class="mb-1 text-muted text-sm">Rôle</label>
                <select class="form-control custom-select" v-model="selectedRole">
                  <option value="all">Tous</option>
                  <option value="owner">Propriétaire</option>
                  <option value="player">Joueur</option>
                </select>
              </div>
            </div>
            
            <nav class="mt-3">
              <ul class="pagination justify-content-center">
                <li class="page-item" :class="{ disabled: currentPage === 1 }">
                  <button class="page-link text-dark" @click="goToPage(currentPage - 1)">
                    Précédent
                  </button>
                </li>
                <li class="page-item" :class="{ disabled: currentPage === totalPages }">
                  <button class="page-link text-dark" @click="goToPage(currentPage + 1)">
                    Suivant
                  </button>
                </li>
              </ul>
            </nav>

            <div v-if="error" class="alert alert-danger">{{ error }}</div>

            <div class="d-flex justify-content-between align-items-center mb-2 text-sm text-muted">
              <span>Utilisateurs listés: {{ filteredUsers.length }}</span>
              <span>Total utilisateurs: {{ users.length }}</span>
            </div>

            <div class="table-responsive text-xs p-0 py-2">
              <table class="table table-hover table-center mb-0">
                <thead class="bg-light">
                  <tr>
                    <th class="text-center" style="width: 52px;">Dét.</th>
                    <th class="pl-4">Utilisateur</th>
                    <th>Email</th>
                    <th>Téléphone</th>
                    <th>Rôle</th>
                    <th class="text-center">Statut</th>
                    <th class="text-right pr-4">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-if="!loading && filteredUsers.length === 0">
                    <td colspan="7" class="text-center text-muted py-4">Aucun utilisateur trouvé.</td>
                  </tr>
                  <tr
                    v-for="user in paginatedUsers"
                    :key="user.id"
                  >
                    <td class="text-center align-middle">
                      <button
                        class="btn btn-link p-0 text-dark"
                        title="Voir détails"
                        aria-label="Voir détails utilisateur"
                        @click.stop="openUserModal(user)"
                      >
                        <span class="material-symbols-outlined">list_alt</span>
                      </button>
                    </td>
                    <td class="pl-4 text-sm">
                      <div class="font-weight-600">{{ displayName(user) }}</div>
                    </td>
                    <td class="text-sm">{{ user.email }}</td>
                    <td class="text-sm">{{ user.phone_number || '-' }}</td>
                    <td class="text-sm">{{ formatRole(user.role) }}</td>
                    <td class="text-center">
                      <span
                        class="badge badge-pill text-xs py-1 px-2"
                        :class="user.is_banned ? 'bg-danger text-white font-weight-bolder' : 'bg-success-light'"
                      >
                        {{ user.is_banned ? 'Bloqué' : 'Débloqué' }}
                      </span>
                    </td>
                    <td class="text-right pr-4">
                      <button
                        class="btn btn-sm text-xs"
                        :class="user.is_banned ? 'btn-success' : 'btn-danger'"
                        :disabled="toggling[user.id]"
                        @click.stop="toggleBan(user)"
                      >
                        <span class="spinner-border spinner-border-sm mr-1" role="status" aria-hidden="true" v-if="toggling[user.id]"></span>
                        {{ user.is_banned ? 'Débloquer' : 'Bloquer' }}
                      </button>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
            <div v-if="loading" class="text-center py-4">
              <span class="spinner-border text-primary" role="status" aria-hidden="true"></span>
            </div>

            <div class="modal fade" id="userDetailsModal" tabindex="-1" aria-hidden="true" style="z-index: 200000;">
              <div class="modal-dialog modal-dialog-centered modal-lg">
                <div class="modal-content">
                  <div class="modal-header">
                    <h5 class="modal-title">Informations utilisateur</h5>
                    <button type="button" class="close" aria-label="Close" @click="closeUserModal">
                      <span class="material-symbols-outlined">close</span>
                    </button>
                  </div>
                  <div class="modal-body" v-if="selectedUser">
                    <div class="row">
                      <div class="col-md-6 mb-3">
                        <div class="text-muted text-xs mb-1">Nom</div>
                        <div class="text-sm font-weight-600">{{ displayName(selectedUser) }}</div>
                      </div>
                      <div class="col-md-6 mb-3">
                        <div class="text-muted text-xs mb-1">Email</div>
                        <div class="text-sm">{{ selectedUser.email || '-' }}</div>
                      </div>
                      <div class="col-md-6 mb-3">
                        <div class="text-muted text-xs mb-1">Téléphone</div>
                        <div class="text-sm">{{ selectedUser.phone_number || '-' }}</div>
                      </div>
                      <div class="col-md-6 mb-3">
                        <div class="text-muted text-xs mb-1">WhatsApp</div>
                        <div class="text-sm">{{ selectedUser.whatsapp_number || '-' }}</div>
                      </div>
                      <div class="col-md-6 mb-3">
                        <div class="text-muted text-xs mb-1">Rôle</div>
                        <div class="text-sm">{{ formatRole(selectedUser.role) }}</div>
                      </div>
                      <div class="col-md-6 mb-3">
                        <div class="text-muted text-xs mb-1">Statut</div>
                        <div class="text-sm">{{ selectedUser.is_banned ? 'Bloqué' : 'Débloqué' }}</div>
                      </div>
                      <div class="col-md-6 mb-1">
                        <div class="text-muted text-xs mb-1">Créé le</div>
                        <div class="text-sm">{{ formatDateTime(selectedUser.created_at) }}</div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import VendorSidebar from '@/components/Vendors/Partials/Sidebar'
import { api } from '@/store'

export default {
  name: 'AdminUsers',
  components: {
    'vendor-sidebar': VendorSidebar,
  },
  data() {
    return {
      users: [],
      loading: false,
      toggling: {},
      error: '',
      searchTerm: '',
      selectedRole: 'all',
      currentPage: 1,
      perPage: 20,
      selectedUser: null,
    }
  },
  computed: {
    currentUser() {
      return this.$store.getters.currentUser
    },
    filteredUsers() {
      const term = this.searchTerm.toLowerCase().trim()
      return this.users
        .filter((user) => {
          if (this.selectedRole !== 'all') {
            return user.role === this.selectedRole
          }
          return true
        })
        .filter((user) => {
          if (!term) return true
          const haystack = [
            user.full_name,
            user.first_name,
            user.last_name,
            user.email,
            user.phone_number,
            user.whatsapp_number,
          ]
            .filter(Boolean)
            .join(' ')
            .toLowerCase()
          return haystack.includes(term)
        })
    },
    totalPages() {
      return Math.max(1, Math.ceil(this.filteredUsers.length / this.perPage))
    },
    paginatedUsers() {
      const start = (this.currentPage - 1) * this.perPage
      return this.filteredUsers.slice(start, start + this.perPage)
    }
  },
  created() {
    this.fetchUsers()
  },
  mounted() {
    const modalEl = document.getElementById('userDetailsModal')
    if (modalEl && modalEl.parentNode !== document.body) {
      document.body.appendChild(modalEl)
    }
  },
  beforeDestroy() {
    const $modal = $('#userDetailsModal')
    if ($modal.length) {
      $modal.modal('hide')
    }
  },
  methods: {
    displayName(user) {
      if (user.full_name) {
        return user.full_name
      }
      const parts = [user.first_name, user.last_name].filter(Boolean)
      if (parts.length) {
        return parts.join(' ')
      }
      return user.email || 'Utilisateur'
    },
    formatRole(role) {
      if (role === 'owner') return 'Propriétaire'
      if (role === 'player') return 'Joueur'
      return role
    },
    async fetchUsers() {
      this.error = ''
      this.loading = true
      try {
        const { data } = await api.get('/users')
        this.users = Array.isArray(data) ? data : []
      } catch (err) {
        this.error = err?.response?.data?.detail || 'Impossible de récupérer les utilisateurs.'
      } finally {
        this.loading = false
      }
    },
    async toggleBan(user) {
      this.error = ''
      this.toggling = { ...this.toggling, [user.id]: true }
      try {
        const { data } = await api.put(`/users/${user.id}/toggle-ban`)
        this.users = this.users.map((item) => (item.id === user.id ? data : item))
      } catch (err) {
        this.error = err?.response?.data?.detail || 'Impossible de mettre à jour le statut.'
      } finally {
        this.toggling = { ...this.toggling, [user.id]: false }
      }
    },
    goToPage(page) {
      if (page >= 1 && page <= this.totalPages) {
        this.currentPage = page
      }
    },
    openUserModal(user) {
      this.selectedUser = user
      this.$nextTick(() => {
        const $modal = $('#userDetailsModal')
        if ($modal.length) {
          if ($modal.parent()[0] !== document.body) {
            $modal.appendTo('body')
          }
          $modal.modal('show')
        }
      })
    },
    closeUserModal() {
      const $modal = $('#userDetailsModal')
      if ($modal.length) {
        $modal.modal('hide')
      }
    },
    formatDateTime(value) {
      if (!value) return '-'
      const date = new Date(value)
      if (Number.isNaN(date.getTime())) return '-'
      return date.toLocaleString('fr-FR', {
        year: 'numeric',
        month: 'short',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit'
      })
    }
  },
  watch: {
    selectedRole() {
      this.currentPage = 1
    },
    searchTerm() {
      this.currentPage = 1
    }
  }
}
</script>
